// Bottom tab bar + layout wrapper for the three primary screens (Main,
// Contacts, Settings). App.tsx wraps only the tab routes in this; in-call,
// add-friend, contact-detail and developer screens render bare (no nav).

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useT, type MessageKey } from '../../i18n/index.js';
import { appUpdateStore, updateIsWaiting } from '../../shell/appUpdate.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from './useStore.js';
import { formatElapsed } from './callFormat.js';
import { displayNameFor } from './peerName.js';

interface Tab {
  to: string;
  labelKey: MessageKey;
  icon: ReactNode;
}

// Inline SVG, not emoji — emoji render inconsistently across OEM fonts (same
// rationale as the InCallScreen control icons).
function TabIcon({ d }: { d: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const TABS: Tab[] = [
  { to: '/', labelKey: 'tabs.main', icon: <TabIcon d="M3 11l9-8 9 8M5 9.5V21h5v-6h4v6h5V9.5" /> },
  {
    to: '/contacts',
    labelKey: 'tabs.contacts',
    icon: (
      <TabIcon d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a7 7 0 0 1 14 0v1M16.5 3.5a4 4 0 0 1 0 7.4M22 21v-1a6.9 6.9 0 0 0-4-6.2" />
    ),
  },
  {
    to: '/settings',
    labelKey: 'tabs.settings',
    icon: (
      <TabIcon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    ),
  },
];

function TabBar(): ReactNode {
  const t = useT();
  // "Update waiting" dot on the Settings tab (APK self-update, docs/06 §4).
  // Always 'idle' on web, so the dot is Android-only by construction.
  const updateWaiting = updateIsWaiting(useStore(appUpdateStore));
  return (
    <nav
      aria-label={t('tabs.primary')}
      // flex-shrink-0: this bar must never be compressed by its flex-column
      // parent — it sits below the scrolling content region (not `sticky`
      // over it), so its height is fixed regardless of how tall that
      // content gets.
      className="mx-auto flex w-full max-w-app flex-shrink-0 border-t border-edge bg-surface"
    >
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end
          className={({ isActive }) =>
            `flex min-h-touch flex-1 flex-col items-center justify-center gap-0.5 py-1 text-xs ${
              isActive ? 'text-accent' : 'text-muted'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span aria-hidden="true" className="relative leading-none">
                {tab.icon}
                {tab.to === '/settings' && updateWaiting && (
                  <span
                    data-testid="update-waiting-dot"
                    className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent"
                  />
                )}
              </span>
              <span aria-current={isActive ? 'page' : undefined}>{t(tab.labelKey)}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

// "Return to call" pill (bug: a user who navigates away from `/call` during a
// live call — e.g. to check Main — otherwise has NO way back short of hunting
// for a stray notification). Shown on every tab screen whenever the engine
// has a call in any non-idle phase; hidden on the call screen itself (which
// TabLayout never wraps anyway, since it isn't a tab route — the pathname
// check is a defensive belt-and-suspenders, not load-bearing). High-visibility
// accent styling so it reads as "something needs your attention", not just
// another nav row.
function ReturnToCallBanner(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { engine } = useScreens();
  const engineState = useStore(engine.state);
  const call = engineState.call;

  // Same ticking pattern as InCallScreen's timer, reused via formatElapsed —
  // only runs once a call is actually connected (no tick before that).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call?.connectedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [call?.connectedAt]);

  if (!call || location.pathname === '/call') return null;

  const name = displayNameFor(call.peerUserId);
  const elapsed = call.connectedAt ? formatElapsed(now - call.connectedAt) : null;
  const label = elapsed
    ? t('call.returnToCallWithTime', { name, time: elapsed })
    : t('call.returnToCall', { name });

  return (
    <button
      type="button"
      aria-label={label}
      data-testid="return-to-call-banner"
      onClick={() => void navigate('/call')}
      className="mx-auto flex min-h-touch w-full max-w-app flex-shrink-0 items-center justify-center gap-2 bg-accent px-4 text-sm font-medium text-ink active:bg-accent-press"
    >
      <PhoneGlyph />
      <span className="truncate">{label}</span>
    </button>
  );
}

// Inline stroke SVG, not emoji — matches the tab-bar / in-call call iconography
// (emoji render inconsistently across OEM fonts). Replaces the former phone
// emoji glyph.
function PhoneGlyph(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.68 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.13a2 2 0 0 1 2.11-.45c.74.32 1.53.55 2.34.68A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

export function TabLayout({ children }: { children: ReactNode }): ReactNode {
  return (
    // `h-full` (a ceiling, via the html/body/#root height:100% chain in
    // index.css), not `min-h-full` (a floor only) — the old min-h-full let
    // this column grow taller than the viewport with tall page content,
    // which made the *document* scroll instead of an inner region. With no
    // scroll container of its own, the tab bar (then `sticky bottom-0`)
    // could be scrolled past its containing block's end and lose its
    // pinning, reading as "squished"/pushed off-screen. `overflow-hidden`
    // here plus `overflow-y-auto` on the content region below confines all
    // scrolling to that region and keeps this column — and the tab bar —
    // pinned to exactly one viewport's height.
    <div className="flex h-full flex-col overflow-hidden">
      <ReturnToCallBanner />
      <div data-scroll-root className="flex-1 overflow-y-auto overscroll-contain">
        {children}
      </div>
      <TabBar />
    </div>
  );
}
